import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface CountryDetail {
  name: string;
  capital: string;
  languages: string[];
  currency: string;
  timezone: string;
  borderCrossings: Array<{
    name: string;
    avgWaitTime: string;
    operatingHours: string;
  }>;
  regulations: string[];
  seasonalConsiderations: string[];
  localPartners: number;
}

interface CountryDetailPanelProps {
  country: CountryDetail;
  onClose: () => void;
}

const CountryDetailPanel = ({ country, onClose }: CountryDetailPanelProps) => {
  return (
    <div className="bg-card rounded-lg shadow-elevation-3 overflow-hidden">
      <div className="bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="font-headline font-bold text-2xl text-primary mb-2">
              {country.name}
            </h2>
            <p className="font-body text-sm text-primary/80">
              Capital: {country.capital}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors duration-300"
            aria-label="Close country details"
          >
            <Icon name="XMarkIcon" size={20} className="text-primary" />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6 max-h-[600px] overflow-y-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <p className="text-xs font-body text-muted-foreground">Languages</p>
            <p className="text-sm font-headline font-semibold text-foreground">
              {country.languages.join(', ')}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-body text-muted-foreground">Currency</p>
            <p className="text-sm font-headline font-semibold text-foreground">
              {country.currency}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-body text-muted-foreground">Timezone</p>
            <p className="text-sm font-headline font-semibold text-foreground">
              {country.timezone}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-body text-muted-foreground">
              Local Partners
            </p>
            <p className="text-sm font-headline font-semibold text-foreground">
              {country.localPartners}
            </p>
          </div>
        </div>

        <div>
          <h3 className="font-headline font-bold text-lg text-foreground mb-3 flex items-center space-x-2">
            <Icon name="MapPinIcon" size={20} className="text-golden-mid" />
            <span>Border Crossings</span>
          </h3>
          <div className="space-y-3">
            {country.borderCrossings.map((crossing, idx) => (
              <div
                key={idx}
                className="p-4 bg-muted rounded-lg border border-border"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-headline font-semibold text-base text-foreground">
                    {crossing.name}
                  </h4>
                  <span className="px-2 py-1 bg-success/10 text-success text-xs font-body rounded">
                    Active
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon
                      name="ClockIcon"
                      size={16}
                      className="text-muted-foreground"
                    />
                    <span className="font-body text-muted-foreground">
                      Avg Wait: {crossing.avgWaitTime}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon
                      name="CalendarIcon"
                      size={16}
                      className="text-muted-foreground"
                    />
                    <span className="font-body text-muted-foreground">
                      {crossing.operatingHours}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-headline font-bold text-lg text-foreground mb-3 flex items-center space-x-2">
            <Icon name="DocumentTextIcon" size={20} className="text-golden-mid" />
            <span>Key Regulations</span>
          </h3>
          <ul className="space-y-2">
            {country.regulations.map((regulation, idx) => (
              <li
                key={idx}
                className="flex items-start space-x-2 font-body text-sm text-muted-foreground"
              >
                <Icon
                  name="CheckCircleIcon"
                  size={16}
                  className="text-success mt-0.5 flex-shrink-0"
                />
                <span>{regulation}</span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-headline font-bold text-lg text-foreground mb-3 flex items-center space-x-2">
            <Icon name="SunIcon" size={20} className="text-golden-mid" />
            <span>Seasonal Considerations</span>
          </h3>
          <div className="space-y-2">
            {country.seasonalConsiderations.map((consideration, idx) => (
              <div
                key={idx}
                className="p-3 bg-warning/10 border border-warning/20 rounded-lg"
              >
                <p className="font-body text-sm text-foreground">
                  {consideration}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryDetailPanel;